/**
 * Replacing a password somebody else chose.
 *
 * Reached two ways: forced, when an administrator created the account or reset
 * it and the temporary password is still in place — nothing else in the product
 * works until then — or voluntarily, from the account menu.
 */
import { useState, type FormEvent } from "react";

import { Lockup } from "@/components/Logo";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";

export function ChangePassword({ forced = false }: { forced?: boolean }) {
  const { adoptToken, refresh, signOut } = useSession();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError("Those two passwords are not the same.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ access_token?: string }>("/auth/me/password", {
        current_password: current,
        new_password: password,
      });
      // The change ends every session including this one, and hands back a
      // replacement. Adopting it is what keeps the person where they are.
      if (result.access_token) await adoptToken(result.access_token);
      else await refresh();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--centred">
      <form className="card auth-card" onSubmit={submit}>
        <div className="card__body stack" style={{ gap: "var(--space-5)" }}>
          <Lockup height={30} />
          <div className="stack" style={{ gap: 4 }}>
            <h1 className="t-h2">
              {forced ? "Choose your own password" : "Change your password"}
            </h1>
            <p className="t-body ink-secondary">
              {forced
                ? "Your account was set up with a temporary password. Choose one only you know before you carry on."
                : "You will be asked to sign in again on your other devices."}
            </p>
          </div>

          {error && <div className="notice notice--error">{error}</div>}

          <div className="field">
            <label className="field__label" htmlFor="current">
              {forced ? "Temporary password" : "Current password"}
            </label>
            <input id="current" className="input" type="password"
              autoComplete="current-password" value={current}
              onChange={(event) => setCurrent(event.target.value)} required />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="new-password">New password</label>
            <input id="new-password" className="input" type="password"
              autoComplete="new-password" value={password}
              onChange={(event) => setPassword(event.target.value)} required />
            <span className={tooShort ? "field__error" : "field__hint"}>
              At least 12 characters.
            </span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="confirm">Confirm new password</label>
            <input id="confirm" className="input" type="password"
              autoComplete="new-password" value={confirm}
              onChange={(event) => setConfirm(event.target.value)} required />
            {mismatch && <span className="field__error">These do not match.</span>}
          </div>

          <button className="btn btn--primary"
            disabled={busy || !current || password.length < 12 || password !== confirm}>
            {busy ? "Saving…" : "Save new password"}
          </button>

          {forced && (
            <button type="button" className="btn btn--ghost" onClick={() => void signOut()}>
              Sign out instead
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
